/**
 * shared.ts
 * ----------------
 * Action creators shared across all portal.
 */

import axios from 'axios';
import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';

import {
    actionCreator, failedAsyncActionCreator, inProgressAsyncActionCreator, onAxiosErrorReceived, payloadActionCreator,
    successfulAsyncActionCreator,
} from './actionCreators';
import {
    Shared_AcknowledgeError, Shared_AcknowledgeSuccess, Shared_ClearHistoryViewContext,
    Shared_ClearLiveSiteEventViewContext, Shared_ReceiveGetActiveLiveSiteEvents, Shared_ReceiveGetLiveSiteEvent,
    Shared_ReceiveGetResolvedLiveSiteEvents, Shared_ReceiveGetStatus, Shared_RequestGetActiveLiveSiteEvents,
    Shared_RequestGetLiveSiteEvent, Shared_RequestGetResolvedLiveSiteEvents, Shared_RequestGetStatus,
    Shared_SetHistoryViewContext, Shared_SetLiveSiteEventViewContext,
} from '../constants/actions';
import { ApiResponse, isMessageReponse } from '../models/dto/common';
import { GetLiveSiteEventResponse, GetStatusResponse, QueryLiveSiteEventsResponse } from '../models/dto/updatePortal';
import { HistoryViewContextPayload } from '../models/payloads/shared';
import { LiveSiteEventSeverity } from '../models/status';
import { TimeRangeValue } from '../models/ui/status';
import { AsyncAction, PayloadAction } from './types';

/**
 * Private action creators
 */

const getActiveLiveSiteEventsRequestFailed = failedAsyncActionCreator(Shared_RequestGetActiveLiveSiteEvents);
const getActiveLiveSiteEventsRequestIssued = inProgressAsyncActionCreator(Shared_RequestGetActiveLiveSiteEvents);
const getActiveLiveSiteEventsRequestSucceeded = successfulAsyncActionCreator(Shared_RequestGetActiveLiveSiteEvents);
const getLiveSiteEventRequestFailed = failedAsyncActionCreator(Shared_RequestGetLiveSiteEvent);
const getLiveSiteEventRequestIssued = inProgressAsyncActionCreator(Shared_RequestGetLiveSiteEvent);
const getLiveSiteEventRequestSucceeded = successfulAsyncActionCreator(Shared_RequestGetLiveSiteEvent);
const getResolvedLiveSiteEventsRequestFailed = failedAsyncActionCreator(Shared_RequestGetResolvedLiveSiteEvents);
const getResolvedLiveSiteEventsRequestIssued = inProgressAsyncActionCreator(Shared_RequestGetResolvedLiveSiteEvents);
const getResolvedLiveSiteEventsRequestSucceeded = successfulAsyncActionCreator(Shared_RequestGetResolvedLiveSiteEvents);
const getStatusRequestFailed = failedAsyncActionCreator(Shared_RequestGetStatus);
const getStatusRequestIssued = inProgressAsyncActionCreator(Shared_RequestGetStatus);
const getStatusRequestSucceeded = successfulAsyncActionCreator(Shared_RequestGetStatus);
const receiveGetActiveLiveSiteEvents = payloadActionCreator<QueryLiveSiteEventsResponse>(Shared_ReceiveGetActiveLiveSiteEvents);
const receiveGetLiveSiteEvent = payloadActionCreator<GetLiveSiteEventResponse>(Shared_ReceiveGetLiveSiteEvent);
const receiveGetResolvedLiveSiteEvents = payloadActionCreator<QueryLiveSiteEventsResponse>(Shared_ReceiveGetResolvedLiveSiteEvents);
const receiveGetStatus = payloadActionCreator<GetStatusResponse>(Shared_ReceiveGetStatus);

/**
 * Action creators
 */

export const acknowledgeError = actionCreator(Shared_AcknowledgeError);
export const acknowledgeSuccess = actionCreator(Shared_AcknowledgeSuccess);
export const clearHistoryViewContext = actionCreator(Shared_ClearHistoryViewContext);
export const clearLiveSiteEventViewContext = actionCreator(Shared_ClearLiveSiteEventViewContext);

export function setHistoryViewContext (
    geographies: string[],
    services: string[],
    severities: LiveSiteEventSeverity[],
    time: TimeRangeValue,
    search: string
): PayloadAction<HistoryViewContextPayload> {
    return {
        payload: {
            geographies,
            search,
            services,
            severities,
            time,
        },
        type: Shared_SetHistoryViewContext,
    };
}

export const setLiveSiteEventViewContext = payloadActionCreator<number>(Shared_SetLiveSiteEventViewContext);

/**
 * Issues an asynchronous action to get the current set of active live site events.
 * @param requireLatestData Does the data need to be the absolute latest?
 * @returns Deferred action creator for getting the current set of active live site events
 */
export function getActiveLiveSiteEvents (requireLatestData: boolean): ThunkAction<Promise<boolean>, unknown, unknown, AsyncAction> {
    return (dispatch: Dispatch) => {
        dispatch(getActiveLiveSiteEventsRequestIssued());

        const params = { st: 'active, mitigated' };
        const url = requireLatestData ? '/api/livesite/admin' : '/api/livesite'; 

        return axios.get<ApiResponse<QueryLiveSiteEventsResponse>>(url, { params })
            .then(response => {
                const { data, status } = response;
                console.info(`Request completed with status code ${status}.`);

                // If error response was given, i.e. if we got a message response, dispatch error action
                if (isMessageReponse(data)) {
                    dispatch(getActiveLiveSiteEventsRequestFailed(data.message));
                    return false;
                }

                dispatch(getActiveLiveSiteEventsRequestSucceeded());
                dispatch(receiveGetActiveLiveSiteEvents(data));

                return true;
            }, onAxiosErrorReceived(dispatch, getActiveLiveSiteEventsRequestFailed))
            .catch(error => {
                console.error(error);
                return false;
            });
    };
}

/**
 * Issues an asynchronous action to get a live site event.
 * @param id Live site event ID
 * @param requireLatestData Does the data need to be the absolute latest?
 * @returns Deferred action creator for getting the live site event
 */
export function getLiveSiteEvent (id: number, requireLatestData: boolean): ThunkAction<Promise<boolean>, unknown, unknown, AsyncAction> {
    return (dispatch: Dispatch) => {
        dispatch(getLiveSiteEventRequestIssued());

        const url = requireLatestData ? `/api/livesite/${id}/admin` : `/api/livesite/${id}`;

        return axios.get<ApiResponse<GetLiveSiteEventResponse>>(url)
            .then(response => {
                const { data, status } = response;
                console.info(`Request completed with status code ${status}.`);

                // If error response was given, i.e. if we got a message response, dispatch error action
                if (isMessageReponse(data)) {
                    dispatch(getLiveSiteEventRequestFailed(data.message));
                    return false;
                }

                dispatch(getLiveSiteEventRequestSucceeded());
                dispatch(receiveGetLiveSiteEvent(data));

                return true;
            }, onAxiosErrorReceived(dispatch, getLiveSiteEventRequestFailed))
            .catch(error => {
                console.error(error);
                return false;
            });
    };
}

/**
 * Issues an asynchronous action to get the current set of resolved live site events.
 * @param time Time range to query over
 * @param requireLatestData Does the data need to be the absolute latest?
 * @returns Deferred action creator for getting the current set of resolved live site events
 */
export function getResolvedLiveSiteEvents (
    time: TimeRangeValue,
    requireLatestData: boolean
): ThunkAction<Promise<boolean>, unknown, unknown, AsyncAction> {
    return (dispatch: Dispatch) => {
        dispatch(getResolvedLiveSiteEventsRequestIssued());

        const params = {
            st: 'resolved',
            t: time,
        };

        const url = requireLatestData ? '/api/livesite/admin' : '/api/livesite';

        return axios.get<ApiResponse<QueryLiveSiteEventsResponse>>(url, { params })
            .then(response => {
                const { data, status } = response;
                console.info(`Request completed with status code ${status}.`);

                // If error response was given, i.e. if we got a message response, dispatch error action
                if (isMessageReponse(data)) {
                    dispatch(getResolvedLiveSiteEventsRequestFailed(data.message));
                    return false;
                }

                dispatch(getResolvedLiveSiteEventsRequestSucceeded());
                dispatch(receiveGetResolvedLiveSiteEvents(data));

                return true;
            }, onAxiosErrorReceived(dispatch, getResolvedLiveSiteEventsRequestFailed))
            .catch(error => {
                console.error(error);
                return false;
            });
    };
}

/**
 * Issues an asynchronous action to refresh the current status.
 * @param requireLatestData Does the data need to be the absolute latest?
 * @returns Deferred action creator for refreshing the status
 */
export function getStatus (requireLatestData: boolean) {
    return (dispatch: Dispatch) => {
        dispatch(getStatusRequestIssued());

        const url = requireLatestData ? '/api/status/admin' : '/api/status';
        
        return axios.get<ApiResponse<GetStatusResponse>>(url)
            .then(response => {
                const { data, status } = response;
                console.info(`Request completed with status code ${status}.`);

                // If error response was given, i.e. if we got a message response, dispatch error action
                if (isMessageReponse(data)) {
                    dispatch(getStatusRequestFailed(data.message));
                    return false;
                }

                dispatch(getStatusRequestSucceeded());
                dispatch(receiveGetStatus(data));
                return true;
            }, onAxiosErrorReceived(dispatch, getStatusRequestFailed))
            .catch(error => {
                console.error(error);
                return false;
            });
    };
}
